/**
 * Forms Settings → Medical History Update — the DEFAULTS for a new office, the inline message slot, the validation, the switch
 * card's block reasons and the "does this link restart the clock" check. Pure.
 * Plan: docs/plans/2026-10-20-forms-settings-plan.md §0 (D7, D11, D13), §1a, §1f item 5, §1h (default text, 12 months), §2.2.
 *
 * ⛔ THE BLOB IS TODAY'S, UNCHANGED: teamio.settings Property 'FORMS_UPDATE_REMINDER_SETTINGS', read by the LIVE forms-service sender
 *    (form-reminder-common.js: settingsEnabled / usableChannels / intervalToMonths / triggerFormIDs) and read-only by
 *    schedule-view-service. Its types are NOT the 'TRUE'/'FALSE' strings of the new blobs — keep them exactly:
 *      activated        JSON BOOLEAN  (the sender requires `=== true`; the modal writes `this.activated`)
 *      channels         { sms: boolean, email: boolean }
 *      intervalNumber   NUMBER 1–99   (the modal writes a number; a blank box can write '' — the sender's parseInt then reads null)
 *      intervalUnit     'months' | 'years'
 *      smsTemplate / emailTemplate   { WikiPageID, Name } (a wiki slot) — or null when the channel is off
 *      triggerForms     [{ FormIDForWeb, Name }]
 *    An INLINE slot adds ONE key and keeps WikiPageID, so the live sender's `usableChannels` still counts it; see the warning below.
 *
 * ⛔ DEPLOY ORDER: the live sender enqueues `smsTemplateID` = the slot's WikiPageID for every usable channel. Until forms-service WP1
 *    teaches it the `inline:medhx:` branch, a saved inline slot would be enqueued as a wiki page id nobody can render. forms-service
 *    first, frontend after (plan §5.3) — never save an inline slot from an editor that is ahead of the sender.
 *
 * ⛔ MEDHX_INLINE_DEFAULTS / MEDHX_DEFAULT_INTERVAL ARE ONE CONSTANT IN THREE REPOS (later): forms-service lib/formsSettings/defaults.js,
 *    teamio-admin (the onboarding seeder, WP3) and THIS file. The forms-service twin test fails — never skips — on drift.
 *
 * ⛔ IMPORTS: only ./formLink (see the header there). Top-level names must stay unique across formLink.js / newPatient.js / medHx.js.
 */

import { isOn, isPlainObject, isFilledText, isValidFormLink, validateMessage, SHARED_BLOCK_MESSAGES } from './formLink';

export const MEDHX_SETTINGS_PROPERTY = 'FORMS_UPDATE_REMINDER_SETTINGS';
export const MEDHX_SMS_INLINE_ID = 'inline:medhx:sms';
export const MEDHX_EMAIL_INLINE_ID = 'inline:medhx:email';

const MEDHX_INLINE_ID_PREFIX = 'inline:medhx:';

export const MEDHX_DEFAULT_SMS_TEXT =
  '{{site_name}}: Hi {{patient_first_name}}, you\'re due to update your Medical & Dental History form before your appointment: {{form_link}}';

export const MEDHX_DEFAULT_EMAIL_SUBJECT = 'Medical History Update';

export const MEDHX_DEFAULT_EMAIL_TEXT =
  'Hello {{patient_first_name}},\n\nYou\'re due to update your Medical & Dental History Form.\n\nPlease use the link below to update your information prior to your appointment.\n\n{{form_link}}\n\nThank you!';

/** The inline slots' default text, shaped like the slot's `inline` object (email: subject + text). */
export const MEDHX_INLINE_DEFAULTS = Object.freeze({
  sms: Object.freeze({ text: MEDHX_DEFAULT_SMS_TEXT }),
  email: Object.freeze({ subject: MEDHX_DEFAULT_EMAIL_SUBJECT, text: MEDHX_DEFAULT_EMAIL_TEXT }),
});

/** A new office asks for an update every 12 months (5 of the 6 live offices; the old popup's 18 is gone). */
export const MEDHX_DEFAULT_INTERVAL = Object.freeze({ intervalNumber: 12, intervalUnit: 'months' });

export const MEDHX_INTERVAL_UNITS = Object.freeze(['months', 'years']);

function pickText(value, fallback) {
  return value === undefined || value === null ? fallback : value;
}

/**
 * A plain-message slot for one channel, in the shape the reminders convention uses:
 *   sms    { WikiPageID:'inline:medhx:sms',   Name:'Plain message', IsSmsTemplate:1, IsEmailTemplate:0, inline:{ text, formLink } }
 *   email  { WikiPageID:'inline:medhx:email', Name:'Plain message', IsSmsTemplate:0, IsEmailTemplate:1, inline:{ subject, text, formLink } }
 * A missing (undefined / null) text or subject becomes the §1h default; an empty string stays empty so validation flags it.
 * `formLink` defaults to null. A fresh mutable object every call.
 */
export function buildMedHxInlineSlot(channel, options) {
  const o = isPlainObject(options) ? options : {};
  const formLink = pickText(o.formLink, null);
  if (channel === 'email') {
    return {
      WikiPageID: MEDHX_EMAIL_INLINE_ID,
      Name: 'Plain message',
      IsSmsTemplate: 0,
      IsEmailTemplate: 1,
      inline: {
        subject: pickText(o.subject, MEDHX_INLINE_DEFAULTS.email.subject),
        text: pickText(o.text, MEDHX_INLINE_DEFAULTS.email.text),
        formLink,
      },
    };
  }
  return {
    WikiPageID: MEDHX_SMS_INLINE_ID,
    Name: 'Plain message',
    IsSmsTemplate: 1,
    IsEmailTemplate: 0,
    inline: { text: pickText(o.text, MEDHX_INLINE_DEFAULTS.sms.text), formLink },
  };
}

function frozenMedHxSlot(channel) {
  const slot = buildMedHxInlineSlot(channel);
  Object.freeze(slot.inline);
  return Object.freeze(slot);
}

/** What a NEW office starts with (OFF, 12 months, both channels on with the default plain messages, no trigger forms yet). */
export const MEDHX_DEFAULTS = Object.freeze({
  activated: false,
  intervalNumber: MEDHX_DEFAULT_INTERVAL.intervalNumber,
  intervalUnit: MEDHX_DEFAULT_INTERVAL.intervalUnit,
  channels: Object.freeze({ sms: true, email: true }),
  smsTemplate: frozenMedHxSlot('sms'),
  emailTemplate: frozenMedHxSlot('email'),
  triggerForms: Object.freeze([]),
});

/** An inline (plain message) slot — its WikiPageID starts with `inline:medhx:`. */
export function isInlineMedHxSlot(slot) {
  return isPlainObject(slot) && typeof slot.WikiPageID === 'string' && slot.WikiPageID.indexOf(MEDHX_INLINE_ID_PREFIX) === 0;
}

/** A wiki-page slot — a non-blank WikiPageID that is not an `inline:` id. The sender's untouched path. */
export function isWikiSlot(slot) {
  return isPlainObject(slot) && isFilledText(slot.WikiPageID) && slot.WikiPageID.indexOf('inline:') !== 0;
}

export const MEDHX_PROBLEM_TEXT = Object.freeze({
  INTERVAL_INVALID: 'Enter a whole number, like 12.',
  UNIT_INVALID: 'Choose months or years.',
  TEMPLATE_MISSING: 'Pick a template or switch to a plain message.',
  NO_CHANNEL: 'Turn on Text or Email.',
  NO_TRIGGER_FORMS: 'Pick at least one form that restarts the clock.',
});

/** "How often": a whole number 1–99, typed (string) or stored (number). */
function isValidMedHxInterval(value) {
  if (typeof value === 'number') return Number.isInteger(value) && value >= 1 && value <= 99;
  if (typeof value !== 'string') return false;
  const text = value.trim();
  if (!/^\d+$/.test(text)) return false;
  const n = Number(text);
  return n >= 1 && n <= 99;
}

/** One ON channel's slot → problems at path `smsTemplate` / `emailTemplate`. */
function medHxSlotProblems(slot, channel, path) {
  if (isInlineMedHxSlot(slot)) {
    const inline = isPlainObject(slot.inline) ? slot.inline : {};
    return validateMessage({ on: 'TRUE', body: inline.text, subject: inline.subject, formLink: inline.formLink }, channel)
      .map((p) => ({ path, code: p.code, message: p.message }));
  }
  if (isWikiSlot(slot)) return [];
  return [{ path, code: 'TEMPLATE_MISSING', message: MEDHX_PROBLEM_TEXT.TEMPLATE_MISSING }];
}

/**
 * The FORMS_UPDATE_REMINDER_SETTINGS blob → every problem, as [{ path, code, message }]. Order: interval, unit, text slot, email slot,
 * then (only when `activated === true`) channels, trigger forms.
 *   intervalNumber                  INTERVAL_INVALID   (1–99, whole; '18' and 18 both pass)
 *   intervalUnit                    UNIT_INVALID
 *   smsTemplate / emailTemplate     only for a channel that is ON — an inline slot runs validateMessage, a wiki slot only needs its
 *                                   WikiPageID, anything else is TEMPLATE_MISSING
 *   channels                        NO_CHANNEL         (activated only)
 *   triggerForms                    NO_TRIGGER_FORMS   (activated only)
 */
export function validateMedHxSettings(blob) {
  const b = isPlainObject(blob) ? blob : {};
  const problems = [];
  const add = (path, code) => problems.push({ path, code, message: MEDHX_PROBLEM_TEXT[code] });

  if (!isValidMedHxInterval(b.intervalNumber)) add('intervalNumber', 'INTERVAL_INVALID');
  if (MEDHX_INTERVAL_UNITS.indexOf(b.intervalUnit) === -1) add('intervalUnit', 'UNIT_INVALID');

  const channels = isPlainObject(b.channels) ? b.channels : {};
  const smsOn = isOn(channels.sms);
  const emailOn = isOn(channels.email);
  if (smsOn) medHxSlotProblems(b.smsTemplate, 'sms', 'smsTemplate').forEach((p) => problems.push(p));
  if (emailOn) medHxSlotProblems(b.emailTemplate, 'email', 'emailTemplate').forEach((p) => problems.push(p));

  if (b.activated === true) {
    if (!smsOn && !emailOn) add('channels', 'NO_CHANNEL');
    const triggers = Array.isArray(b.triggerForms) ? b.triggerForms : [];
    if (!triggers.some((f) => isPlainObject(f) && isFilledText(f.FormIDForWeb))) add('triggerForms', 'NO_TRIGGER_FORMS');
  }
  return problems;
}

/**
 * Why the Medical History switch cannot be turned on — every reason that applies, in the order the card shows them (the FIRST is the
 * one shown). Evaluated as if `activated` were true: this is the "can it be turned on" check.
 *   ctx = { smsReady:Boolean, emailReady:Boolean }
 *   MESSAGE_PROBLEMS  a message that is on has a problem (blank, no / two / unpicked form link, unknown variable, too long, no template)
 *   INTERVAL_INVALID  "How often" is not a whole number 1–99 (or the unit is not months / years)   [not in the brief; see the report]
 *   NO_TRIGGER_FORMS  no form restarts the clock
 *   SMS_NOT_READY     Text is on and the site has no texting number
 *   EMAIL_NOT_READY   Email is on and the site has no verified sender
 *   NOTHING_ON        neither channel is on
 * @returns {Array<{ code:string, message:string }>}
 */
export function medHxBlockReasons(blob, ctx) {
  const b = isPlainObject(blob) ? blob : {};
  const c = isPlainObject(ctx) ? ctx : {};
  const problems = validateMedHxSettings(Object.assign({}, b, { activated: true }));
  const reasons = [];

  const isMessageProblem = (p) => p.path === 'smsTemplate' || p.path === 'emailTemplate';
  if (problems.some(isMessageProblem)) reasons.push({ code: 'MESSAGE_PROBLEMS', message: SHARED_BLOCK_MESSAGES.MESSAGE_PROBLEMS });

  const intervalProblem = problems.find((p) => p.path === 'intervalNumber' || p.path === 'intervalUnit');
  if (intervalProblem) reasons.push({ code: 'INTERVAL_INVALID', message: intervalProblem.message });

  if (problems.some((p) => p.code === 'NO_TRIGGER_FORMS')) {
    reasons.push({ code: 'NO_TRIGGER_FORMS', message: MEDHX_PROBLEM_TEXT.NO_TRIGGER_FORMS });
  }

  const channels = isPlainObject(b.channels) ? b.channels : {};
  const smsOn = isOn(channels.sms);
  const emailOn = isOn(channels.email);
  if (smsOn && !c.smsReady) reasons.push({ code: 'SMS_NOT_READY', message: SHARED_BLOCK_MESSAGES.SMS_NOT_READY });
  if (emailOn && !c.emailReady) reasons.push({ code: 'EMAIL_NOT_READY', message: SHARED_BLOCK_MESSAGES.EMAIL_NOT_READY });
  if (!smsOn && !emailOn) reasons.push({ code: 'NOTHING_ON', message: SHARED_BLOCK_MESSAGES.NOTHING_ON });
  return reasons;
}

/**
 * Does a patient who fills the linked form restart the update clock? (§1f item 5 — the amber "Submitting this form won't restart the
 * clock" warning shows when this is false.)
 *   formLink       the message's choice `{ kind, id, name }`; null / malformed ⇒ true (nothing to warn about)
 *   triggerForms   the blob's `triggerForms` — [{ FormIDForWeb, Name }]
 *   packetFormIds  for a packet link: the FormIDForWeb of every form in THAT packet. teamio.`form-packets`.FormIDs is stored as a JSON
 *                  text array of FormIDForWeb strings (forms-service forms-api.js: insert ~5908, read ~528); POST /get-packets
 *                  (store forms/getPackets) returns each packet's `FormIDs` already parsed to an array — pass that array as is.
 * form → true when its id is a trigger form; packet → true when ANY of its forms is a trigger form (all compared as strings).
 */
export function linkRestartsClock(formLink, triggerForms, packetFormIds) {
  if (!isValidFormLink(formLink)) return true;
  const triggerIds = (Array.isArray(triggerForms) ? triggerForms : [])
    .filter((f) => isPlainObject(f) && isFilledText(f.FormIDForWeb))
    .map((f) => String(f.FormIDForWeb));
  if (formLink.kind === 'form') return triggerIds.indexOf(formLink.id) !== -1;
  return (Array.isArray(packetFormIds) ? packetFormIds : []).some((id) => triggerIds.indexOf(String(id)) !== -1);
}
